import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

// =============================================
// Announcement Enums
// =============================================

export enum AnnouncementStatus {
  Drafted = 'Drafted',
  Published = 'Published',
  PublishedWithNotification = 'PublishedWithNotification',
}

export enum AnnouncementAudience {
  Everyone = 'Everyone',
  MSP = 'MSP',
  Agencies = 'Agencies',
  Companies = 'Companies',
  Candidates = 'Candidates',
}

export enum AnnouncementAction {
  SaveDraft = 'SaveDraft',
  Publish = 'Publish',
  PublishWithNotifications = 'PublishWithNotifications',
}

// =============================================
// Announcement Types
// =============================================

export interface Announcement {
  id: string
  title: string
  content: string
  audience: AnnouncementAudience[]
  /** MSP agencies picked on the MSPs tab; empty with the MSP audience means every MSP. */
  targetMspIds?: string[]
  targetAgencyIds: string[]
  targetCompanyIds: string[]
  /** Legacy: site targeting is no longer offered, and saving an announcement clears it. */
  targetSiteIds?: string[]
  targetCandidateIds?: string[]
  attachments?: AnnouncementAttachment[]
  status: AnnouncementStatus
  publishedDate: string | null
  createdBy: string
  createdByName: string
  modifiedBy: string
  createdDate: string
  modifiedDate: string
  announcementTargets?: AnnouncementCompanyTarget[]
  mspTargets?: AnnouncementMspTarget[]
  people?: AnnouncementPeople
  allCompanies?: boolean
}

export interface AnnouncementCompanyTarget {
  companyId: string
  companyName: string
  companySites: number
  sitesTarget: string[]
}

export interface AnnouncementMspTarget {
  mspId: string
  mspName: string
}

export interface AnnouncementPeople {
  audiences: AnnouncementAudience[]
  candidateNames: string[]
}

export interface AnnouncementAttachment {
  id: string
  name: string
  extension: string
  contentType?: string
}

export interface CreateAnnouncementRequest {
  title: string
  content: string
  audience: AnnouncementAudience[]
  targetMspIds?: string[]
  targetAgencyIds?: string[]
  targetCompanyIds?: string[]
  targetSiteIds?: string[]
  targetCandidateIds?: string[]
  attachments?: AnnouncementAttachment[]
  action: AnnouncementAction
}

export interface UpdateAnnouncementRequest {
  title?: string
  content?: string
  audience?: AnnouncementAudience[]
  targetMspIds?: string[]
  targetAgencyIds?: string[]
  targetCompanyIds?: string[]
  targetSiteIds?: string[]
  targetCandidateIds?: string[]
  attachments?: AnnouncementAttachment[]
}

export interface AnnouncementCandidate {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  last4Ssn: string
}

export type CandidateSearchBy = 'name' | 'ssn' | 'email' | 'phone'

export interface AnnouncementCandidatePagingResponse {
  candidates: AnnouncementCandidate[]
  total: number
  page: number
  pageSize: number
}

export interface PublishAnnouncementRequest {
  sendNotification: boolean
}

export interface AnnouncementPagingParams {
  pageNumber: number
  pageSize: number
  search?: string
  status?: AnnouncementStatus
  audience?: AnnouncementAudience
  startDate?: string
  endDate?: string
}

export interface AnnouncementPagingResponse {
  data: Announcement[]
  total: number
  page: number
  limit: number
}

export interface AnnouncementSeenResponse {
  lastSeenDate: string
  unseenCount: number
}

export interface CandidateAnnouncement {
  id: string
  title: string
  content: string
  createdByName?: string
  audience: AnnouncementAudience[]
  status: AnnouncementStatus
  publishedDate?: string
  createdDate?: string
  attachments: AnnouncementAttachment[]
}

export interface CandidateAnnouncementPagingParams {
  pageNumber: number
  pageSize: number
  search?: string
}

export interface CandidateAnnouncementPagingResponse {
  items: CandidateAnnouncement[]
  total: number
}

// =============================================
// Announcements API Service
// =============================================

class AnnouncementsApiService extends BaseApi {
  private readonly basePath = '/api/announcements'

  async list(params: AnnouncementPagingParams): Promise<AnnouncementPagingResponse> {
    try {
      const queryParams = new URLSearchParams()
      queryParams.append('pageNumber', params.pageNumber.toString())
      queryParams.append('pageSize', params.pageSize.toString())
      if (params.search) queryParams.append('search', params.search)
      if (params.status) queryParams.append('status', params.status)
      if (params.audience) queryParams.append('audience', params.audience)
      if (params.startDate) queryParams.append('startDate', params.startDate)
      if (params.endDate) queryParams.append('endDate', params.endDate)

      const response = await this.get<{
        data?: Announcement[]
        items?: Announcement[]
        total?: number
        page?: number
        pageNumber?: number
        limit?: number
        pageSize?: number
      }>(`${this.basePath}?${queryParams.toString()}`)
      const body = response.data
      return {
        data: body.data ?? body.items ?? [],
        total: body.total ?? 0,
        page: body.page ?? body.pageNumber ?? 1,
        limit: body.limit ?? body.pageSize ?? params.pageSize,
      }
    } catch (error) {
      reportCatchError('AnnouncementsApi.list failed:', error)
      throw error
    }
  }

  async getById(id: string): Promise<Announcement> {
    try {
      const response = await this.get<Announcement>(`${this.basePath}/${id}`)
      return response.data
    } catch (error) {
      reportCatchError('AnnouncementsApi.getById failed:', error)
      throw error
    }
  }

  async create(request: CreateAnnouncementRequest): Promise<Announcement> {
    try {
      const response = await this.post<Announcement>(this.basePath, request)
      return response.data
    } catch (error) {
      reportCatchError('AnnouncementsApi.create failed:', error)
      throw error
    }
  }

  async uploadAttachment(file: File): Promise<AnnouncementAttachment> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.post<AnnouncementAttachment>(`${this.basePath}/files`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    })
    return response.data
  }

  async uploadInlineImage(file: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.post<string>(`${this.basePath}/public-images`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    })
    return response.data
  }

  async downloadAttachment(announcementId: string, fileId: string): Promise<Blob> {
    const response = await this.get<Blob>(
      `${this.basePath}/${announcementId}/attachments/${fileId}`,
      {responseType: 'blob'},
    )
    return response.data
  }

  async listForCandidate(
    params: CandidateAnnouncementPagingParams,
  ): Promise<CandidateAnnouncementPagingResponse> {
    const query = new URLSearchParams({
      pageNumber: String(params.pageNumber),
      pageSize: String(params.pageSize),
    })
    if (params.search) query.set('search', params.search)
    const response = await this.get<CandidateAnnouncementPagingResponse>(
      `/candidate-portal/announcements?${query.toString()}`,
    )
    return response.data
  }

  async getCandidateAnnouncement(id: string): Promise<CandidateAnnouncement> {
    const response = await this.get<CandidateAnnouncement>(`/candidate-portal/announcements/${id}`)
    return response.data
  }

  async downloadCandidateAttachment(announcementId: string, fileId: string): Promise<Blob> {
    const response = await this.get<Blob>(
      `/candidate-portal/announcements/${announcementId}/attachments/${fileId}`,
      {responseType: 'blob'},
    )
    return response.data
  }

  async searchCandidates(
    search: string,
    pageNumber: number,
    pageSize: number,
    searchBy: CandidateSearchBy = 'name',
  ): Promise<AnnouncementCandidatePagingResponse> {
    const params = new URLSearchParams({
      search,
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
      searchBy,
    })
    const response = await this.get<AnnouncementCandidatePagingResponse>(
      `${this.basePath}/candidates?${params.toString()}`,
    )
    return response.data
  }

  async getCandidateIds(search: string, searchBy: CandidateSearchBy = 'name'): Promise<string[]> {
    const params = new URLSearchParams({search, searchBy})
    const response = await this.get<{ids: string[]}>(
      `${this.basePath}/candidate-ids?${params.toString()}`,
    )
    return response.data.ids
  }

  async update(id: string, request: UpdateAnnouncementRequest): Promise<Announcement> {
    try {
      const response = await this.put<Announcement>(`${this.basePath}/${id}`, request)
      return response.data
    } catch (error) {
      reportCatchError('AnnouncementsApi.update failed:', error)
      throw error
    }
  }

  async publish(id: string, request: PublishAnnouncementRequest): Promise<Announcement> {
    try {
      const response = await this.patch<Announcement>(`${this.basePath}/${id}/publish`, request)
      return response.data
    } catch (error) {
      reportCatchError('AnnouncementsApi.publish failed:', error)
      throw error
    }
  }

  async discard(id: string): Promise<void> {
    try {
      await this.delete(`${this.basePath}/${id}`)
    } catch (error) {
      reportCatchError('AnnouncementsApi.discard failed:', error)
      throw error
    }
  }

  async markSeen(): Promise<undefined> {
    try {
      await this.post(`${this.basePath}/mark-seen`, {})
    } catch (error) {
      reportCatchError('AnnouncementsApi.markSeen failed:', error)
      throw error
    }
  }

  async getUnseenCount(): Promise<{count: number}> {
    try {
      const response = await this.get<{count: number}>(`${this.basePath}/unseen-count`)
      return response.data
    } catch (error) {
      reportCatchError('AnnouncementsApi.getUnseenCount failed:', error)
      throw error
    }
  }
}

export const announcementsApi = new AnnouncementsApiService()
